/* ═══════════════════════════════════════════════════════════════════════════
   L'ordre de rendu, en couches (PLAN.md §4.5).

   Ce n'est pas de l'organisation : c'est une décision de gameplay. Dans un
   shmup, ce qui n'est pas lisible n'est pas jouable, et la lisibilité est
   d'abord une question d'ordre de dessin.

   La couche qui compte est DANGER : les projectiles ennemis sont rendus dans
   une scène séparée, APRÈS toute la chaîne de post-processing, sans test de
   profondeur, sans bloom, en palette réservée. Trois bénéfices d'un coup :

     1. Lisibilité — la couche danger est toujours nette, toujours au-dessus,
        jamais noyée dans le halo.
     2. Immunité au resolution scale — l'overlay est rendu à la résolution
        native même quand le reste tombe à 0,5. Les balles restent nettes
        exactement au moment où le jeu dégrade, c'est-à-dire quand ça chauffe.
     3. Fidélité colorimétrique — les balles ne traversent ni ACES ni la LUT,
        donc leur couleur est exactement celle qui a été autorisée. Cela
        neutralise au passage le hue-skew d'ACES sur les magentas et bleus
        saturés : l'objection principale à ACES disparaît sur la seule couche
        où elle aurait compté.

   Le bloom sur les projectiles ennemis est LE péché mortel du shmup néon
   amateur : ils fusionnent en bouillie lumineuse dès la deuxième vague, et le
   symptôme n'apparaît qu'après l'ajout des VFX — quand la refonte coûte une
   semaine.
   ═══════════════════════════════════════════════════════════════════════════ */

export const Layer = {
  /** Ciel, fond lointain. */
  SkyFar: 0,
  /** Décor moyen, sol. */
  DecorMid: 100,
  /** ⚠️ Voile sombre posé sur TOUT le décor. Sans lui, la luminance du fond
   *  finit par concurrencer celle des projectiles et la lecture s'effondre. */
  Scrim: 500,
  Enemies: 600,
  Player: 1000,
  PlayerBullets: 1200,
  /** Rendus dans un target séparé, en demi-résolution. */
  Vfx: 1500,
  Pickups: 2000,
  /** Avant-plan décoratif, alpha ≤ 0,35, jamais collisionnable. */
  DecorFront: 2500,
  /** ⚠️ Scène OVERLAY, après le post-processing. */
  Danger: 3000,
  /** Vaisseau re-rendu, point de hitbox, anneau de graze. */
  PlayerOverlay: 3500,
} as const;

export type LayerName = keyof typeof Layer;

/** Couches rendues dans la scène overlay, donc APRÈS le post-processing. */
export const OVERLAY_LAYERS: readonly number[] = [Layer.Danger, Layer.PlayerOverlay];

/** Les projectiles ennemis doivent être strictement au-dessus de tout ce qui
 *  vit dans la scène principale. Vérifié en CI. */
export function dangerIsOnTop(): boolean {
  const main = [
    Layer.SkyFar,
    Layer.DecorMid,
    Layer.Scrim,
    Layer.Enemies,
    Layer.Player,
    Layer.PlayerBullets,
    Layer.Vfx,
    Layer.Pickups,
    Layer.DecorFront,
  ];
  return main.every((v) => v < Layer.Danger);
}

/** Ordre de rendu d'un projectile ennemi.
 *
 *  On décale par la vitesse : les petites balles rapides passent au-dessus des
 *  grosses lentes. Quand deux projectiles se superposent, celui qui arrive vite
 *  est celui qu'il faut voir en premier — c'est lui qui laisse le moins de temps
 *  pour réagir. */
export function bulletRenderOrder(speed: number): number {
  return Layer.Danger + Math.round(speed);
}
